import React from 'react';
import { motion } from 'framer-motion';
import { FaGavel, FaShoppingCart, FaTruck, FaCreditCard, FaShieldAlt, FaRuler } from 'react-icons/fa';
import './TermsOfService.css';

const TermsOfService = () => {
  return (
    <div className="terms-of-service">
      {/* Hero Section */}
      <section className="terms-hero">
        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Terms of Service</h1>
            <p>Please read these terms carefully. They govern your use of our website and services.</p>
            <div className="last-updated">
              <FaGavel />
              <span>Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="terms-content">
        <div className="container">
          <div className="content-wrapper">
            
            {/* Introduction */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Introduction</h2>
              <p>Welcome to Vastré By Vijh. These Terms of Service ("Terms") govern your access to and use of our website, products, and services. By accessing or using our website, you agree to be bound by these Terms and all applicable laws and regulations.</p>
              <p>If you do not agree with any of these terms, you are prohibited from using or accessing this website. The materials contained in this website are protected by applicable copyright and trademark law.</p>
            </motion.div>

            {/* Acceptance of Terms */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h2>Acceptance of Terms</h2>
              <p>By accessing and using our website, you accept and agree to be bound by the terms and provision of this agreement. In addition, when using our website's particular services, you shall be subject to any posted guidelines or rules applicable to such services.</p>
              <p>Any participation in this service will constitute acceptance of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
            </motion.div>

            {/* Account Registration */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h2>Account Registration and Security</h2>
              <p>To access certain features of our website, you may be required to create an account. You agree to:</p>
              <ul>
                <li><strong>Provide accurate information:</strong> All registration information must be truthful and current</li>
                <li><strong>Maintain security:</strong> Keep your password confidential and secure</li>
                <li><strong>Account responsibility:</strong> You are responsible for all activities under your account</li>
                <li><strong>Prompt notification:</strong> Notify us immediately of any unauthorized use</li>
              </ul>
              <p>We reserve the right to terminate accounts that violate these terms or are used for fraudulent purposes.</p>
            </motion.div>

            {/* Product Information */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h2>
                <FaShoppingCart />
                Product Information and Pricing
              </h2>
              <p>We strive to provide accurate product information, but we do not warrant that product descriptions, colors, information, or other content available on our website is accurate, complete, reliable, current, or error-free.</p>
              
              <h3>Pricing</h3>
              <ul>
                <li><strong>All prices are in Indian Rupees (INR)</strong> unless otherwise stated</li>
                <li><strong>Prices are subject to change</strong> without prior notice</li>
                <li><strong>Prices may vary</strong> according to size chart and customizations</li>
                <li><strong>Taxes and shipping</strong> are calculated at checkout</li>
                <li><strong>Price matching</strong> is not available</li>
              </ul>

              <h3>Product Availability</h3>
              <ul>
                <li><strong>Limited Edition:</strong> All pieces are limited edition bespoke couture</li>
                <li><strong>Inventory levels</strong> are updated in real-time</li>
                <li><strong>Out-of-stock items</strong> will be clearly marked</li>
                <li><strong>Custom orders</strong> require consultation and may have extended timelines</li>
                <li><strong>Bespoke nature:</strong> Each piece is crafted to order with premium materials</li>
              </ul>
            </motion.div>

            {/* Ordering and Payment */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h2>
                <FaCreditCard />
                Ordering and Payment Terms
              </h2>
              <p>By placing an order, you agree to pay the full amount specified at checkout. We accept various payment methods including:</p>
              <ul>
                <li><strong>Credit/Debit Cards:</strong> Visa, MasterCard, American Express</li>
                <li><strong>Digital Wallets:</strong> UPI, Google Pay, PhonePe</li>
                <li><strong>Net Banking:</strong> Major Indian banks</li>
                <li><strong>EMI Options:</strong> Available on select cards</li>
              </ul>

              <h3>Order Confirmation</h3>
              <ul>
                <li><strong>Order receipt:</strong> You will receive an email confirmation</li>
                <li><strong>Payment verification:</strong> Orders are processed after payment confirmation</li>
                <li><strong>Order changes:</strong> Modifications must be requested within 2 hours of placement</li>
                <li><strong>Order cancellation:</strong> Available before processing begins</li>
              </ul>

              <h3>Payment Security</h3>
              <p>All payments are processed through secure, encrypted payment gateways. We do not store your complete payment information on our servers.</p>
            </motion.div>

            {/* Shipping and Delivery */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h2>
                <FaTruck />
                Shipping and Delivery
              </h2>
              <p>Once your order is received, our team will contact you to confirm your size and measurements. This step ensures your garment is tailored to provide the perfect fit, reflecting the premium craftsmanship you expect.</p>
              
              <h3>Order Processing</h3>
              <ul>
                <li><strong>Size confirmation:</strong> Our team contacts you to confirm measurements</li>
                <li><strong>Perfect fit guarantee:</strong> Ensures your garment is tailored correctly</li>
                <li><strong>Premium craftsmanship:</strong> Reflects the quality you expect</li>
                <li><strong>Processing period:</strong> 10-14 business days for careful crafting</li>
              </ul>

              <h3>Delivery Timeframes</h3>
              <ul>
                <li><strong>Processing time:</strong> 10-14 business days after order confirmation</li>
                <li><strong>Shipping time:</strong> 15-20 business days depending on location</li>
                <li><strong>Location dependent:</strong> Delivery times vary by destination</li>
                <li><strong>Customs processing:</strong> Additional time for international shipments</li>
              </ul>

              <h3>Shipping Costs</h3>
              <ul>
                <li><strong>Free shipping</strong> on orders above ₹2,500</li>
                <li><strong>Additional charges:</strong> For orders below ₹2,500 or expedited shipping</li>
                <li><strong>Expedited shipping:</strong> Available for additional charges</li>
                <li><strong>International shipping:</strong> Calculated based on destination</li>
              </ul>

              <h3>Quality Standards</h3>
              <ul>
                <li><strong>Highest quality:</strong> We strive to ensure your customized or premium order reaches you promptly while maintaining the highest quality standards</li>
                <li><strong>External factors:</strong> Shipping times may vary slightly due to external courier factors or customs processing for international shipments</li>
                <li><strong>Customer patience:</strong> We appreciate your patience and promise the wait will be worth it for exceptional craftsmanship and a perfect fit</li>
                <li><strong>Delivery tracking:</strong> Real-time updates provided via email/SMS</li>
              </ul>

              <h3>Delivery Terms</h3>
              <ul>
                <li><strong>Delivery confirmation:</strong> Signature may be required</li>
                <li><strong>Delivery attempts:</strong> Up to 3 attempts will be made</li>
                <li><strong>Failed delivery:</strong> Items returned to sender after 3 attempts</li>
                <li><strong>Location-based:</strong> Delivery times depend on your specific location</li>
              </ul>
            </motion.div>

            {/* Returns and Alterations */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>
                <FaRuler />
                Returns and Alterations Policy
              </h2>
              <p>As all our products are made-to-order with premium craftsmanship, we do not offer traditional returns or refunds. However, to guarantee your satisfaction, we provide expert alteration services if the size does not fit as expected.</p>
              
              <h3>No Returns or Refunds</h3>
              <ul>
                <li><strong>Made-to-order:</strong> All pieces are custom-crafted to your specifications</li>
                <li><strong>Premium craftsmanship:</strong> High-quality fabrics and expert tailoring are non-refundable</li>
                <li><strong>Custom measurements:</strong> Pieces are tailored specifically for you</li>
                <li><strong>Unique pieces:</strong> Each piece is crafted individually and cannot be resold</li>
              </ul>

              <h3>Alteration Services</h3>
              <ul>
                <li><strong>Alteration window:</strong> 7 days from receiving your order</li>
                <li><strong>Return process:</strong> Ship the item back to us at your cost</li>
                <li><strong>Expert assessment:</strong> Our tailors will assess and discuss required adjustments with you</li>
                <li><strong>Communication:</strong> We'll contact you via phone or email to discuss alterations</li>
                <li><strong>Perfect fit guarantee:</strong> We will expertly tailor the garment to ensure a perfect fit</li>
                <li><strong>Return shipping:</strong> We will promptly return the altered item to you</li>
              </ul>

              <h3>Return Conditions</h3>
              <ul>
                <li><strong>Original condition:</strong> Item must be returned unworn and unwashed</li>
                <li><strong>Tags intact:</strong> All original tags must be present</li>
                <li><strong>Time limit:</strong> Alteration requests must be made within 7 days of receiving your order</li>
                <li><strong>Quality assurance:</strong> We stand behind our craftsmanship and will ensure your satisfaction</li>
              </ul>

              <h3>Quality Assurance</h3>
              <ul>
                <li><strong>Pre-delivery inspection:</strong> All pieces undergo quality checks</li>
                <li><strong>Material verification:</strong> Premium fabrics are verified before crafting</li>
                <li><strong>Craftsmanship guarantee:</strong> We stand behind our couture quality</li>
                <li><strong>Customer satisfaction:</strong> We work to ensure your complete satisfaction</li>
              </ul>
            </motion.div>

            {/* Intellectual Property */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              viewport={{ once: true }}
            >
              <h2>Intellectual Property Rights</h2>
              <p>All content on this website, including but not limited to text, graphics, logos, images, audio clips, digital downloads, data compilations, and software, is the property of Vastré By Vijh or its content suppliers and is protected by Indian and international copyright laws.</p>
              
              <h3>Permitted Use</h3>
              <ul>
                <li><strong>Personal use:</strong> Viewing and purchasing for personal consumption</li>
                <li><strong>Educational purposes:</strong> Learning about our products and services</li>
                <li><strong>Reference:</strong> Citing our content with proper attribution</li>
              </ul>

              <h3>Prohibited Use</h3>
              <ul>
                <li><strong>Commercial reproduction:</strong> Using our content for commercial purposes</li>
                <li><strong>Modification:</strong> Altering or modifying our content</li>
                <li><strong>Distribution:</strong> Sharing our content without permission</li>
                <li><strong>Reverse engineering:</strong> Attempting to copy our technology or processes</li>
              </ul>
            </motion.div>

            {/* User Conduct */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>User Conduct and Responsibilities</h2>
              <p>When using our website, you agree to:</p>
              
              <h3>Acceptable Behavior</h3>
              <ul>
                <li><strong>Respectful communication:</strong> Be courteous in all interactions</li>
                <li><strong>Accurate information:</strong> Provide truthful and current information</li>
                <li><strong>Legal compliance:</strong> Use our services in accordance with applicable laws</li>
                <li><strong>Security awareness:</strong> Protect your account and personal information</li>
              </ul>

              <h3>Prohibited Activities</h3>
              <ul>
                <li><strong>Fraudulent behavior:</strong> Attempting to deceive or mislead</li>
                <li><strong>Unauthorized access:</strong> Attempting to access restricted areas</li>
                <li><strong>Spam and harassment:</strong> Sending unwanted communications</li>
                <li><strong>Malicious software:</strong> Introducing viruses or harmful code</li>
                <li><strong>Impersonation:</strong> Pretending to be someone else</li>
              </ul>
            </motion.div>

            {/* Limitation of Liability */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              viewport={{ once: true }}
            >
              <h2>
                <FaShieldAlt />
                Limitation of Liability
              </h2>
              <p>To the maximum extent permitted by law, Vastré By Vijh shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:</p>
              <ul>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Personal injury or property damage</li>
                <li>Emotional distress or mental anguish</li>
                <li>Any damages resulting from your use of our website</li>
              </ul>
              
              <p>Our total liability to you for any claims arising from these terms or your use of our website shall not exceed the amount you paid for the specific product or service giving rise to the claim.</p>
            </motion.div>

            {/* Dispute Resolution */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              viewport={{ once: true }}
            >
              <h2>Dispute Resolution</h2>
              <p>In the event of any dispute arising from these terms or your use of our website, we encourage you to contact us first to resolve the issue amicably.</p>
              
              <h3>Resolution Process</h3>
              <ul>
                <li><strong>Direct communication:</strong> Contact our customer service team</li>
                <li><strong>Escalation:</strong> If not resolved, escalate to management</li>
                <li><strong>Mediation:</strong> Consider third-party mediation if needed</li>
                <li><strong>Legal action:</strong> As a last resort, pursue legal remedies</li>
              </ul>

              <h3>Governing Law</h3>
              <p>These terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in New Delhi, India.</p>
            </motion.div>

            {/* Changes to Terms */}
            <motion.div
              className="terms-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.1 }}
              viewport={{ once: true }}
            >
              <h2>Changes to Terms of Service</h2>
              <p>We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting on our website. We will notify users of significant changes through:</p>
              <ul>
                <li><strong>Website notification:</strong> Prominent notices on our homepage</li>
                <li><strong>Email communication:</strong> Notifications to registered users</li>
                <li><strong>Account alerts:</strong> Messages in user account dashboards</li>
              </ul>
              
              <p>Your continued use of our website after any changes constitutes acceptance of the updated terms. We encourage you to review these terms periodically.</p>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              className="terms-section contact-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              viewport={{ once: true }}
            >
              <h2>Contact Information</h2>
              <p>If you have any questions about these Terms of Service, please contact us:</p>
              
              <div className="contact-info">
                <div className="contact-item">
                  <strong>Email:</strong> legal@vastrebvijh.com
                </div>
                <div className="contact-item">
                  <strong>Address:</strong> PROPERTY NO. 55 THIRD FLOOR, SAIDULAJAB NEW DELHI 110030, NEAR WESTEND MARGIN LANE-2
                </div>
              </div>
              
              <p>We will respond to your inquiry within 48 hours and work to resolve any concerns you may have about our terms of service.</p>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
